/* ============================================================
   SAM-714 — full-page event editor (/eventsad/new, /eventsad/:eventId).

   Replaces the create/edit drawer that lived in EventsList.tsx. Same fields,
   laid out like the service editor (ServiceEditorPage, SAM-198): sticky header
   with the actions, sectioned cards below. The old «Active» checkbox becomes a
   Status select (Draft / Published / Archived):

   - Approvers (Cognito groups `admin` / `xhinola_admin`, isEventApprover) pick
     any status and see «Publish» on drafts.
   - Everyone else saves drafts only; a Cap Cana admin reviews and publishes.
     Once an event is published or archived only an approver can change it —
     the CRUD lambda enforces both rules (SAM-713), this page only mirrors them.

   SAM-710: the Mobile Banner upload is enabled (banner_mobile_url is a real
   column now) and no longer stripped from the payload.

   Tailwind + tokens + src/components/ui only (BaseUI/Styletron freeze, SAM-175).
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Bold, CalendarDays, Eye, EyeOff, FileText, Image as ImageIcon, Italic, Link2,
  Copy, List as ListIcon, ListOrdered, Loader2, Send, Tags, Trash2,
} from 'lucide-react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { worksAsFrontDesk } from '../../utils/frontDeskMatrix';
import {
  useEvent, useEventMutations, useEventTypes, useEventGroups, eventStatusOf, type EventStatus,
} from '../../hooks/useEvents';
import { useTenantIds } from '../../hooks/useTenant';
import { useBusinesses } from '../../hooks/useBusinessAccessType';
import { useAccessSettings } from '../../hooks/useAccessSettings';
import { accessSettingOptions } from './accessSettingOptions';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { Notice } from '../../components/ui/Notice';
import { toaster } from '../../components/ui/toast';
import { renderMarkdownLite } from '../../utils/markdownLite';
import { Field, Grid2, SectionCard } from '../ServiceEditor/ServiceEditorPage';
import {
  EMPTY_EVENT_FORM, STATUS_META, STATUS_ORDER, createdEventId, eventToForm,
  formToPayload, formatEventDateTime, missingFieldsHint, missingRequiredFields, resolveSaveStatus,
  syncFormWithServer, type EventForm, type RequiredEventField,
} from './eventForm';
import { EventStatusBadge } from './EventStatusBadge';
import { NewEventWizard } from './create/NewEventWizard';
import { applyMarkdownEdit, type MarkdownKind } from '../../utils/markdownEdit';
import { DateTimeField } from '../../components/ui/DateTimeField';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { isPrivateEventTypeId } from '../../utils/privateEvent';
import { useFdCan } from '../../hooks/useFdCan';

const LANG_FIELDS = [
  { en: 'name_en', es: 'name_es' },
  { en: 'short_description_en', es: 'short_description_es' },
  { en: 'description_en', es: 'description_es' },
] as const;

/** Router state carried between the editor and the preview so unsaved edits survive the round trip. */
export interface EventDraftState {
  draft?: EventForm;
}

const mdBtn: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 6, border: '1px solid var(--line)', background: '#fff',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-700)', cursor: 'pointer',
};

export const EventEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams<{ eventId?: string }>();
  const isCreate = !eventId || eventId === 'new';
  const restored = (location.state as EventDraftState | null)?.draft;

  const permissions = useUserPermissions();
  const { isEventApprover, isLoading: permsLoading } = permissions;
  // The estate group roles cannot list access_settings either (SAM-1108).
  const asFrontDesk = worksAsFrontDesk(permissions);
  const canEditEventMatrix = useFdCan('events', 'edit');
  const {
    data: event, isLoading: eventLoading, isError, refetch: refetchEvent,
  } = useEvent(isCreate ? undefined : eventId);
  const { createEvent, updateEvent, deleteEvent, isCreating, isUpdating } = useEventMutations();
  const { currentTenantId } = useTenantIds();

  const { data: businessesData } = useBusinesses();
  const businesses = useMemo(() => {
    const list = Array.isArray(businessesData) ? businessesData : (businessesData as any)?.data || [];
    return [...list].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '')) as Array<{ id: string; name: string }>;
  }, [businessesData]);
  const { data: eventTypes } = useEventTypes();
  const { data: eventGroups } = useEventGroups();
  // The tenant Front Desk may not list the access_settings table (403): it gets the event's own setting inline, from
  // the event's expand, and never asks for the list. Everyone else lists them to choose one.
  const { data: accessSettings } = useAccessSettings({ enabled: !asFrontDesk });
  const accessOptions = useMemo(
    () => accessSettingOptions(accessSettings, event?.access_settings),
    [accessSettings, event?.access_settings],
  );

  const [form, setForm] = useState<EventForm>(() => restored ?? { ...EMPTY_EVENT_FORM, tenant_id: currentTenantId || undefined });
  // The server copy the form was last synced with (null until loaded; restored-from-preview edits are dirty against it).
  const [baseline, setBaseline] = useState<EventForm | null>(() => (restored ? null : form));
  const [langTab, setLangTab] = useState<'en' | 'es'>('en');
  const [descPreview, setDescPreview] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const descWrapRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const businessSelectRef = useRef<HTMLSelectElement | null>(null);

  // Always start from fresh server data: the cached copy can predate a save or a
  // publish made from the Preview page (dev QA 01-10).
  useEffect(() => {
    if (!isCreate) void refetchEvent();
  }, [isCreate, eventId, refetchEvent]);

  // Keep the form in step with the server copy. Untouched forms take it whole;
  // forms with unsaved edits keep them but follow a newer server status
  // (syncFormWithServer), so «Save draft» can never silently unpublish.
  const serverForm = useMemo(() => (event ? eventToForm(event, eventStatusOf(event)) : null), [event]);
  const serverKey = serverForm ? JSON.stringify(serverForm) : '';
  useEffect(() => {
    if (isCreate || !serverForm) return;
    if (baseline && JSON.stringify(baseline) === serverKey) return;
    // Restored-from-preview edits are kept as they are on the first load.
    setForm((prev) => (baseline ? syncFormWithServer(prev, baseline, serverForm) : restored ? prev : serverForm));
    setBaseline(serverForm);
    // serverKey stands in for serverForm (same content → same key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, serverKey]);

  const savedStatus: EventStatus = isCreate ? 'draft' : eventStatusOf(event);
  // Non-approvers can only work on drafts; a published/archived event is read-only for them.
  // The tenant Front Desk views events but does not change them (specs/front-desk-tenant-permisos.md).
  const canEdit = (isEventApprover || isCreate || savedStatus === 'draft') && canEditEventMatrix;
  const isDirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const isSaving = isCreating || isUpdating || checkingStatus;
  // SAM-730: the Event code field exists only for the Private event type (read-only, SAM-737).
  const isPrivate = isPrivateEventTypeId(form.event_type_id, eventTypes);
  const missing = missingRequiredFields(form);
  const isValid = missing.length === 0;
  const fieldError = (field: RequiredEventField) =>
    showErrors ? missing.find((m) => m.field === field)?.message : undefined;

  const updateField = <K extends keyof EventForm>(field: K, value: EventForm[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /** Show the inline errors and bring the first missing field into view. */
  const revealMissing = () => {
    setShowErrors(true);
    const first = missing[0];
    const el = first?.field === 'name' ? nameInputRef.current
      : first?.field === 'business_id' ? businessSelectRef.current
        : document.querySelector<HTMLElement>(`[aria-label="${first?.field === 'end_date' ? 'End' : 'Start'}"]`);
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  };

  const save = async (requested: EventStatus) => {
    if (!isValid) { revealMissing(); return; }
    let status = requested;
    if (!isCreate) {
      // Never send a status older than the server's: re-read it right before saving.
      setCheckingStatus(true);
      const latest = await refetchEvent().then((r) => r.data ?? event, () => event).finally(() => setCheckingStatus(false));
      status = resolveSaveStatus(requested, baseline?.status, latest ? eventStatusOf(latest) : undefined);
    }
    const payload = formToPayload({ ...form, status }, isEventApprover ? status : 'draft');
    try {
      if (isCreate) {
        const res = await createEvent(payload);
        const newId = createdEventId(res);
        const saved = { ...form, status: payload.status as EventStatus };
        setBaseline(saved);
        setForm(saved);
        navigate(newId ? `/eventsad/${newId}` : '/eventsad/list', { replace: true, state: null });
      } else {
        await updateEvent({ id: eventId!, data: payload });
        const saved = { ...form, status: payload.status as EventStatus };
        setForm(saved);
        setBaseline(saved);
        if (restored) navigate(location.pathname, { replace: true, state: null });
      }
    } catch {
      // useEventMutations already shows the error toast.
    }
  };

  const handleBack = () => {
    if (isDirty && !window.confirm('Discard your unsaved changes?')) return;
    navigate('/eventsad/list');
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    if (isCreate) return;
    await deleteEvent(eventId!);
    navigate('/eventsad/list');
  };

  const openPreview = () => {
    navigate(`/eventsad/${isCreate ? 'new' : eventId}/preview`, { state: { draft: form } satisfies EventDraftState });
  };

  const autoFill = () => {
    const source = langTab === 'en' ? 'es' : 'en';
    let filled = 0;
    const next = { ...form } as Record<string, any>;
    LANG_FIELDS.forEach((f) => {
      const from = f[source];
      const to = f[langTab];
      if (next[from] && !next[to]) { next[to] = next[from]; filled++; }
    });
    setForm(next as EventForm);
    const name = source === 'en' ? 'English' : 'Spanish';
    if (filled > 0) toaster.positive(`Copied ${filled} field${filled > 1 ? 's' : ''} from ${name}`, { autoHideDuration: 2000 });
    else toaster.warning(`Nothing to copy — ${name} is empty or these fields already have content`, { autoHideDuration: 2000 });
  };

  const nameField = langTab === 'en' ? 'name_en' : 'name_es';
  const shortField = langTab === 'en' ? 'short_description_en' : 'short_description_es';
  const descField = langTab === 'en' ? 'description_en' : 'description_es';

  // SAM-305: Markdown marks for the Description textarea (same behaviour as the old drawer).
  const applyMd = (kind: MarkdownKind) => {
    const el = descWrapRef.current?.querySelector('textarea');
    const value = (form[descField] || '') as string;
    if (!el) return;
    const edit = applyMarkdownEdit(value, el.selectionStart ?? value.length, el.selectionEnd ?? value.length, kind,
      kind === 'link' ? window.prompt('Link URL', 'https://') ?? '' : undefined);
    if (!edit) return;
    updateField(descField, edit.value);
    setTimeout(() => { el.focus(); el.setSelectionRange(edit.selStart, edit.selEnd); }, 0);
  };

  // SAM-1072: creating an event is the four-step wizard (create/NewEventWizard.tsx); the editor below is for existing events.
  if (isCreate) {
    return (
      <NewEventWizard
        form={form}
        setForm={setForm}
        initialStep={restored ? 'review' : 'event'}
        businesses={businesses}
        eventTypes={(eventTypes || []).map((t) => ({ id: t.id, label: t.name || `Type #${t.id}` }))}
        eventGroups={(eventGroups || []).map((g) => ({ id: g.id, label: g.name || `Group #${g.id}` }))}
        accessOptions={accessOptions}
        isPrivate={isPrivate}
        isEventApprover={isEventApprover}
        isSaving={isSaving}
        onSave={(requested) => save(requested)}
        onPreview={openPreview}
        onCancel={handleBack}
      />
    );
  }

  if (!isCreate && (eventLoading || permsLoading) && !restored) {
    return (
      <div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--ink-500)' }}>
        <Loader2 className="animate-spin mr-2" size={16} /> Loading event…
      </div>
    );
  }
  if (!isCreate && !restored && (isError || !event)) {
    return (
      <div className="p-8 text-sm" style={{ color: 'var(--ink-500)' }}>
        <p>Could not load this event.</p>
        <button type="button" className="btn ghost mt-3" onClick={() => navigate('/eventsad/list')}>
          <ArrowLeft size={14} /> Back to Events
        </button>
      </div>
    );
  }

  const displayName = form.name_en || form.name_es || (isCreate ? 'New event' : 'Untitled event');
  const status = form.status;
  const saveLabel = status === 'draft' ? 'Save draft' : status === 'published' ? 'Save · goes live' : 'Save';
  const showPublish = isEventApprover && status === 'draft';
  const infoLine = !isEventApprover
    ? (canEdit
      ? 'Saved as a draft. A Cap Cana admin reviews and publishes it.'
      : `${STATUS_META[savedStatus].label}: only a Cap Cana admin can change this event.`)
    : status === 'draft'
      ? 'Draft: not visible on capcana.app. Check it in Preview, then Publish.'
      : status === 'published'
        ? 'Published: changes go live on capcana.app as soon as you save.'
        : 'Archived: not visible on capcana.app.';

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--surface)' }}>
      <header
        className="flex items-center gap-3 px-6 py-3 shrink-0 bg-white flex-wrap"
        style={{ borderBottom: '1px solid var(--line)', position: 'relative', zIndex: 10 }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back to Events"
          style={{
            width: 36, height: 36, borderRadius: 'var(--r-8)', border: '1px solid var(--line)', borderStyle: 'solid',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
            color: 'var(--ink-700)', flexShrink: 0, cursor: 'pointer',
          }}
        >
          <ArrowLeft size={16} />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="text-[18px] font-semibold truncate m-0" style={{ color: 'var(--ink-900)' }}>{displayName}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <EventStatusBadge status={savedStatus} />
            {!isCreate && event?.published_at && savedStatus === 'published' && (
              <span className="text-xs" style={{ color: 'var(--ink-500)' }}>Published {formatEventDateTime(event.published_at)}</span>
            )}
            <span className="text-xs inline-flex items-center gap-1.5" style={{ color: isDirty ? 'var(--warn-fg)' : 'var(--ink-400)' }}>
              {isDirty && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warn-fg)' }} />}
              {isDirty ? 'Unsaved changes' : isCreate ? '' : 'All changes saved'}
            </span>
          </div>
        </div>

        <label className="flex items-center gap-2 text-[12px] font-semibold" style={{ color: 'var(--ink-500)' }}>
          Status
          <select
            aria-label="Status"
            value={isEventApprover ? status : (canEdit ? 'draft' : savedStatus)}
            disabled={!isEventApprover || isSaving}
            onChange={(e) => updateField('status', e.target.value as EventStatus)}
            className="text-[13px] font-semibold"
            style={{
              padding: '8px 10px', borderRadius: 'var(--r-8)', border: '1px solid var(--line)', borderStyle: 'solid',
              background: '#fff', color: 'var(--ink-900)', cursor: isEventApprover ? 'pointer' : 'not-allowed',
            }}
          >
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </label>

        <button type="button" className="btn ghost" onClick={openPreview}>
          <Eye size={14} /> Preview
        </button>
        {canEdit && (
          <button
            type="button"
            className={showPublish ? 'btn ghost' : 'btn primary'}
            onClick={() => save(status)}
            disabled={isSaving}
            title={missingFieldsHint(missing, 'save') || undefined}
          >
            {isSaving ? 'Saving…' : saveLabel}
          </button>
        )}
        {showPublish && (
          // Wrapper carries the tooltip: browsers don't show `title` on a disabled button.
          <span title={missingFieldsHint(missing, 'publish') || undefined} className="inline-flex">
            <button
              type="button"
              className="btn primary"
              onClick={() => save('published')}
              disabled={!isValid || isSaving}
              aria-describedby={!isValid ? 'event-missing-hint' : undefined}
            >
              <Send size={14} /> Publish
            </button>
          </span>
        )}
        {canEdit && !isValid && (
          <button
            type="button"
            id="event-missing-hint"
            onClick={revealMissing}
            className="basis-full text-right text-[12px] m-0 p-0 border-0 bg-transparent cursor-pointer"
            style={{ color: showErrors ? 'var(--bad-fg)' : 'var(--ink-500)' }}
          >
            {missingFieldsHint(missing, showPublish ? 'publish' : 'save')}
          </button>
        )}
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6 pb-16">
          <Notice variant="info" className="mb-4">{infoLine}</Notice>

          <fieldset disabled={!canEdit} className="m-0 p-0 border-0" style={{ minWidth: 0 }}>
            <SectionCard title="Event information" icon={<FileText size={14} />}>
              <nav className="osec-nav" aria-label="Language" style={{ marginBottom: 14 }}>
                <button type="button" className={langTab === 'en' ? 'on' : ''} onClick={() => setLangTab('en')}>EN</button>
                <button type="button" className={langTab === 'es' ? 'on' : ''} onClick={() => setLangTab('es')}>ES</button>
                <button
                  type="button"
                  onClick={autoFill}
                  className="inline-flex items-center gap-1 text-[12px]"
                  style={{ marginLeft: 'auto', color: 'var(--ink-500)' }}
                >
                  <Copy size={13} /> Copy from {langTab === 'en' ? 'Spanish' : 'English'}
                </button>
              </nav>

              <Field label="Name" required error={fieldError('name')}>
                <input
                  ref={nameInputRef}
                  aria-invalid={!!fieldError('name') || undefined}
                  value={(form[nameField] as string) || ''}
                  onChange={(e) => updateField(nameField, e.target.value)}
                  placeholder={langTab === 'en' ? 'Event name' : 'Nombre del evento'}
                />
              </Field>
              <Field label="Short description">
                <input
                  value={(form[shortField] as string) || ''}
                  onChange={(e) => updateField(shortField, e.target.value)}
                  placeholder={langTab === 'en' ? 'Brief description' : 'Descripción breve'}
                />
              </Field>
              <Field
                label="Description"
                caption={langTab === 'en' ? 'Markdown: bold, italic, headings (##), lists and links' : 'Markdown: negrita, cursiva, títulos (##), listas y enlaces'}
              >
                <div ref={descWrapRef}>
                  <div className="flex items-center gap-1 mb-1.5">
                    <button type="button" title="Bold" style={mdBtn} onClick={() => applyMd('bold')}><Bold size={14} /></button>
                    <button type="button" title="Italic" style={mdBtn} onClick={() => applyMd('italic')}><Italic size={14} /></button>
                    <button type="button" title="Heading 2" aria-label="Heading 2" style={{ ...mdBtn, fontSize: 11, fontWeight: 700 }} onClick={() => applyMd('heading')}>H2</button>
                    <button type="button" title="Heading 3" aria-label="Heading 3" style={{ ...mdBtn, fontSize: 11, fontWeight: 700 }} onClick={() => applyMd('heading3')}>H3</button>
                    <button type="button" title="Bullet list" style={mdBtn} onClick={() => applyMd('bullet')}><ListIcon size={14} /></button>
                    <button type="button" title="Numbered list" style={mdBtn} onClick={() => applyMd('numbered')}><ListOrdered size={14} /></button>
                    <button type="button" title="Link" style={mdBtn} onClick={() => applyMd('link')}><Link2 size={14} /></button>
                    <button
                      type="button"
                      onClick={() => setDescPreview((p) => !p)}
                      className="inline-flex items-center gap-1 text-[12px]"
                      style={{ ...mdBtn, width: 'auto', padding: '0 10px', marginLeft: 'auto' }}
                    >
                      {descPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                      {descPreview ? 'Edit' : 'Preview'}
                    </button>
                  </div>
                  {descPreview ? (
                    <div
                      className="text-[14px]"
                      style={{ minHeight: 100, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', lineHeight: 1.6, color: '#333' }}
                    >
                      {renderMarkdownLite((form[descField] as string) || '') || (
                        <span style={{ color: 'var(--ink-400)' }}>{langTab === 'en' ? 'Nothing to preview yet' : 'Nada que previsualizar aún'}</span>
                      )}
                    </div>
                  ) : (
                    <textarea
                      value={(form[descField] as string) || ''}
                      onChange={(e) => updateField(descField, e.target.value)}
                      placeholder={langTab === 'en' ? 'Full description' : 'Descripción completa'}
                      style={{ minHeight: 120 }}
                    />
                  )}
                </div>
              </Field>
            </SectionCard>

            <SectionCard title="Classification" icon={<Tags size={14} />}>
              <Field label="Business" required caption="Associates the event with a business" error={fieldError('business_id')}>
                <select
                  ref={businessSelectRef}
                  aria-invalid={!!fieldError('business_id') || undefined}
                  value={form.business_id || ''}
                  onChange={(e) => updateField('business_id', e.target.value)}
                >
                  <option value="">Select a business…</option>
                  {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Grid2>
                <Field label="Event type" caption="Categorizes the event in the app">
                  <select
                    value={form.event_type_id != null ? String(form.event_type_id) : ''}
                    onChange={(e) => updateField('event_type_id', e.target.value ? Number(e.target.value) : undefined)}
                  >
                    <option value="">No type</option>
                    {(eventTypes || []).map((t) => <option key={t.id} value={String(t.id)}>{t.name || `Type #${t.id}`}</option>)}
                  </select>
                </Field>
                <Field label="Event group" caption="Groups the event for filtering in the app">
                  <select
                    value={form.event_group_id != null ? String(form.event_group_id) : ''}
                    onChange={(e) => updateField('event_group_id', e.target.value ? Number(e.target.value) : undefined)}
                  >
                    <option value="">No group</option>
                    {(eventGroups || []).map((g) => <option key={g.id} value={String(g.id)}>{g.name || `Group #${g.id}`}</option>)}
                  </select>
                </Field>
              </Grid2>
              <Field label="Access settings" caption="Companion rules — adults/children limits">
                <select
                  value={form.access_settings_id != null ? String(form.access_settings_id) : ''}
                  onChange={(e) => updateField('access_settings_id', e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">None</option>
                  {accessOptions.map((a: { id: number; label: string }) => <option key={a.id} value={String(a.id)}>{a.label}</option>)}
                </select>
              </Field>
            </SectionCard>

            <SectionCard title="Event details" icon={<CalendarDays size={14} />}>
              <Field label="Address">
                <input value={form.address || ''} onChange={(e) => updateField('address', e.target.value)} />
              </Field>
              <Grid2>
                <Field label="Start" required error={fieldError('start_date')}>
                  <DateTimeField ariaLabel="Start" invalid={!!fieldError('start_date')} value={form.start_date || ''} onChange={(v) => updateField('start_date', v)} />
                </Field>
                <Field label="End" required error={fieldError('end_date')}>
                  <DateTimeField ariaLabel="End" invalid={!!fieldError('end_date')} value={form.end_date || ''} onChange={(v) => updateField('end_date', v)} />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Capacity">
                  <input type="number" value={form.capacity ?? 0} onChange={(e) => updateField('capacity', parseInt(e.target.value, 10) || 0)} />
                </Field>
                <Field label="Phone number">
                  <input value={form.phone_number || ''} onChange={(e) => updateField('phone_number', e.target.value)} />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Instagram">
                  <input value={form.instagram_url || ''} onChange={(e) => updateField('instagram_url', e.target.value)} placeholder="@handle or URL" />
                </Field>
                <Field label="Website">
                  <input value={form.website_url || ''} onChange={(e) => updateField('website_url', e.target.value)} placeholder="https://" />
                </Field>
              </Grid2>
              {isPrivate && (
                <Grid2>
                  {/* SAM-737: the server generates the code on create; it cannot be edited. */}
                  <Field
                    label="Event code"
                    caption={form.pin_code
                      ? 'Guests enter this code to register'
                      : isCreate ? 'The code is generated when you save the event' : 'The code is not available'}
                  >
                    {form.pin_code && (
                      <div className="flex items-center gap-2">
                        <input aria-label="Event code" value={form.pin_code} readOnly autoComplete="off" />
                        <button
                          type="button"
                          aria-label="Copy event code"
                          title="Copy event code"
                          className="shrink-0 p-2 rounded"
                          style={{ color: 'var(--ink-500)' }}
                          onClick={() => {
                            navigator.clipboard?.writeText(form.pin_code || '').then(
                              () => toaster.positive('Event code copied', { autoHideDuration: 2000 }),
                              () => toaster.warning('Could not copy the event code', { autoHideDuration: 2000 }),
                            );
                          }}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    )}
                  </Field>
                </Grid2>
              )}
            </SectionCard>

            <SectionCard title="Images" icon={<ImageIcon size={14} />}>
              <div className="mb-4">
                <S3ImageUpload
                  value={form.logo_url || ''}
                  onChange={(key) => updateField('logo_url', key)}
                  label="Event Logo"
                  previewHeight="100px"
                  objectFit="contain"
                />
              </div>
              <p className="text-[12px] mt-0 mb-3" style={{ color: 'var(--ink-500)' }}>
                Banners are shown at the top of the event page. Upload one or both — if only one is set, it is used for both layouts.
              </p>
              <div className="mb-4">
                <S3ImageUpload
                  value={form.background_img_url || ''}
                  onChange={(key) => updateField('background_img_url', key)}
                  label="Desktop Banner"
                  previewHeight="140px"
                  objectFit="cover"
                />
                <div className="text-[12px] mt-1.5" style={{ color: 'var(--ink-500)' }}>
                  Recommended 1920×600px (16:5). PNG, JPG or WEBP, max 5MB.
                </div>
              </div>
              <div>
                <S3ImageUpload
                  value={form.banner_mobile_url || ''}
                  onChange={(key) => updateField('banner_mobile_url', key)}
                  label="Mobile Banner"
                  previewHeight="180px"
                  objectFit="cover"
                />
                <div className="text-[12px] mt-1.5" style={{ color: 'var(--ink-500)' }}>
                  Recommended 1080×1350px (4:5 portrait). PNG, JPG or WEBP, max 5MB.
                </div>
              </div>
            </SectionCard>
          </fieldset>

          {!isCreate && canEdit && (
            <SectionCard title="Danger zone" icon={<Trash2 size={14} />}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-[13px]" style={{ color: 'var(--ink-700)' }}>
                  Delete this event and its settings. This cannot be undone.
                </span>
                <button type="button" className="btn danger" onClick={() => setConfirmDelete(true)}>
                  <Trash2 size={14} /> Delete event
                </button>
              </div>
            </SectionCard>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete event"
        message="This event will be deleted. This cannot be undone."
        confirmText="Delete event"
      />
    </div>
  );
};

export default EventEditorPage;
